/**
 * Front-my-End — Live preview scheduling (pure, no DOM).
 *
 * CSS is hot-swapped without reloading the preview, so it applies after a short
 * pause. HTML re-renders the whole preview, so it waits for a longer pause and
 * supersedes any pending CSS swap (the render carries the current CSS). Editor
 * JavaScript never schedules a run: half-typed JS is routinely an infinite loop
 * (`while (i < n) {` before `i++`), so JS runs only when the user presses Run.
 */

export const LIVE_CSS_DEBOUNCE_MS = 100;
export const LIVE_HTML_DEBOUNCE_MS = 800;

export function createLiveScheduler({
  run,
  isEnabled,
  onJsEdit = () => {},
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (id) => clearTimeout(id)
}) {
  let cssTimer = null;
  let htmlTimer = null;

  function cancelCss() {
    if (cssTimer !== null) clearTimer(cssTimer);
    cssTimer = null;
  }

  function cancelHtml() {
    if (htmlTimer !== null) clearTimer(htmlTimer);
    htmlTimer = null;
  }

  return {
    /** Called on every editor input with the edited language. */
    schedule(lang) {
      if (lang === 'js') {
        onJsEdit();
        return;
      }
      if (!isEnabled()) return;

      if (lang === 'css') {
        cancelCss();
        cssTimer = setTimer(() => {
          cssTimer = null;
          run();
        }, LIVE_CSS_DEBOUNCE_MS);
        return;
      }

      if (lang === 'html') {
        cancelHtml();
        htmlTimer = setTimer(() => {
          htmlTimer = null;
          cancelCss();
          run();
        }, LIVE_HTML_DEBOUNCE_MS);
      }
    },

    cancel() {
      cancelCss();
      cancelHtml();
    }
  };
}
